import { Text, Tooltip } from '@mantine/core';
import type { DataLibraryCoachSurveyRow, DataLibrarySurveyQuestion } from '../../../types';
import { SurveyResponsePill } from '../components/SurveyResponsePill';
import { renderCohortHealthBadge } from '../components/CohortHealthBadge';

export function renderCoachSurveyCell(
  row: DataLibraryCoachSurveyRow,
  columnId: string,
  questions: DataLibrarySurveyQuestion[],
): React.ReactNode {
  switch (columnId) {
    case 'cohort_name':
      return <Text size="sm">{row.cohort_name}</Text>;
    case 'cohort_health':
      return renderCohortHealthBadge(row.cohort_health);
    case 'full_name':
      return <Text size="sm">{row.full_name}</Text>;
    case 'email':
      return (
        <Text size="sm" c="dimmed" truncate>
          {row.email}
        </Text>
      );
    case 'submitted_at': {
      if (!row.submitted_at) return <Text size="xs" c="dimmed">—</Text>;
      const date = new Date(row.submitted_at);
      const formatted = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return (
        <Tooltip label={date.toLocaleString()} withArrow>
          <Text size="sm">{formatted}</Text>
        </Tooltip>
      );
    }
    case 'survey_stage':
      return <Text size="sm" fw={500}>{row.survey_stage || '—'}</Text>;
    case 'overall_satisfaction':
      if (!row.overall_satisfaction) {
        return <Text size="xs" c="dimmed">—</Text>;
      }
      return <SurveyResponsePill value={row.overall_satisfaction} questionType="scale" />;
    default: {
      // Dynamic question columns (prefixed with q_)
      if (columnId.startsWith('q_')) {
        const questionId = columnId.slice(2);
        const value = row.responses[questionId];
        const question = questions.find(q => q.question_id === questionId);

        if (!value) {
          return <Text size="xs" c="dimmed">—</Text>;
        }

        return (
          <SurveyResponsePill
            value={value}
            questionType={question?.question_type || 'text'}
          />
        );
      }
      return <Text size="sm">—</Text>;
    }
  }
}
